import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Flex, Select, SelectProps, Spin, Tag } from "antd";
import { useTranslation } from "react-i18next";

import { items as requirementItemsFetch, list as requirementList } from "@/apis/requirement";
import {
  Requirement,
  RequirementConfirmTypeEnum,
  RequirementConfirmValueEnum,
  RequirementItem,
  RequirementStatusEnum,
} from "@/types/requirement";
import SText from "@/styles/SText";

type RequirementSelectBaseProps = Omit<SelectProps<string>, "options" | "onChange"> & {
  onChange?: (value?: string) => void;
};

type RequirementSelectProps =
  | (RequirementSelectBaseProps & {
      entityType: "requirement";
      onEntityChange?: (entity: Requirement | null) => void;
    })
  | (RequirementSelectBaseProps & {
      entityType: "item";
      requirementId?: string;
      excludeIds?: string[];
      onEntityChange?: (entity: RequirementItem | null) => void;
    });

interface RequirementSelectOption {
  value: string;
  label: string;
  entity: Requirement | RequirementItem;
}

interface RequirementListCacheEntry {
  items: Requirement[];
  total: number;
}

const REQUIREMENT_PAGE_SIZE = 20;
const REQUIREMENT_PASSED_STATES = [
  {
    type: RequirementConfirmTypeEnum.MATERIAL_CONTROL,
    value: RequirementConfirmValueEnum.PASSED,
  },
  {
    type: RequirementConfirmTypeEnum.COMMODITY,
    value: RequirementConfirmValueEnum.PASSED,
  },
  {
    type: RequirementConfirmTypeEnum.PROD,
    value: RequirementConfirmValueEnum.PASSED,
  },
  {
    type: RequirementConfirmTypeEnum.PURCHASE,
    value: RequirementConfirmValueEnum.PASSED,
  },
  {
    type: RequirementConfirmTypeEnum.FINANCE,
    value: RequirementConfirmValueEnum.PASSED,
  },
  {
    type: RequirementConfirmTypeEnum.SALE,
    value: RequirementConfirmValueEnum.PASSED,
  },
];

const requirementItemsCache = new Map<string, RequirementItem[]>();
const requirementItemsRequestCache = new Map<string, Promise<RequirementItem[]>>();
// 需求单列表缓存：按关键字 + 页码缓存结果。
const requirementListCache = new Map<string, RequirementListCacheEntry>();
// 在途请求缓存：同一个 key 并发触发时只发一次请求。
const requirementListRequestCache = new Map<string, Promise<RequirementListCacheEntry>>();

// 列表有分页，缓存 key 需要同时区分 keyword 和 page。
const getRequirementListCacheKey = (keyword: string, page: number) => `${keyword}::${page}`;

const mergeById = <T extends { id: string }>(...groups: Array<Array<T> | undefined>) => {
  const optionMap = new Map<string, T>();

  groups.forEach((group) => {
    group?.forEach((item) => {
      if (item?.id) {
        optionMap.set(item.id, item);
      }
    });
  });

  return Array.from(optionMap.values());
};

const formatRequirementLabel = (item: Requirement, t: (key: string) => string) =>
  `${t("sale.innerNumber")}: ${item.saleOrder?.innerNumber || "-"} / ${t("requirement.number")}: ${item.number || "-"}`;

// 选中态同时带出名称/型号/编码，避免相近物料只显示名称时难以区分。
const formatRequirementItemLabel = (item: RequirementItem, t: (key: string) => string) =>
  `${item.product?.name || "-"} / ${item.product?.model || "-"} / ${item.product?.code || "-"}`;

const matchRequirementItemKeyword = (item: RequirementItem, keyword: string) => {
  const lowerKeyword = keyword.toLowerCase();
  return [
    item.product?.code,
    item.product?.name,
    item.product?.model,
    item.useAmount?.toString(),
    item.outCount?.toString(),
  ].some((field) => field?.toLowerCase().includes(lowerKeyword));
};

const RequirementSelect: FC<RequirementSelectProps> = (props) => {
  const { t } = useTranslation();
  const {
    entityType,
    onChange,
    onEntityChange,
    requirementId,
    excludeIds,
    disabled,
    dropdownRender: dropdownRenderProp,
    onSearch: onSearchProp,
    onPopupScroll: onPopupScrollProp,
    onDropdownVisibleChange: onDropdownVisibleChangeProp,
    ...selectProps
  } = props as RequirementSelectProps & {
    requirementId?: string;
    excludeIds?: string[];
  };
  const value = typeof selectProps.value === "string" ? selectProps.value : undefined;

  const [listLoading, setListLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [selectedRequirement, setSelectedRequirement] = useState<Requirement | null>(null);
  const [requirementItems, setRequirementItems] = useState<RequirementItem[]>([]);

  const requirementFetchIdRef = useRef(0);
  const itemFetchIdRef = useRef(0);
  const latestRequirementIdRef = useRef(requirementId);

  const isItemSelect = entityType === "item";

  useEffect(() => {
    latestRequirementIdRef.current = requirementId;
  }, [requirementId]);

  useEffect(() => {
    if (!value && entityType === "requirement") {
      setSelectedRequirement(null);
    }
  }, [entityType, value]);

  useEffect(() => {
    if (entityType === "item") {
      setKeyword("");
      setRequirementItems([]);
    }
  }, [entityType, requirementId]);

  // 编辑回显：有选中值时预加载该需求单的清单项，使 value 能立即解析为标签，
  // 而不是在选项加载前显示原始 id
  useEffect(() => {
    if (isItemSelect && requirementId && value) {
      loadRequirementItems(requirementId);
    }
  }, [isItemSelect, requirementId, value]);

  const fetchRequirements = (nextKeyword = "", page = 1) => {
    const fetchId = ++requirementFetchIdRef.current;
    const cacheKey = getRequirementListCacheKey(nextKeyword, page);

    // 统一应用列表结果，避免缓存命中和网络返回各写一套。
    const applyResult = ({ items, total: nextTotal }: RequirementListCacheEntry) => {
      if (fetchId !== requirementFetchIdRef.current) {
        return;
      }

      setTotal(nextTotal);
      setRequirements((prev) => {
        if (page === 1) {
          return mergeById(selectedRequirement ? [selectedRequirement] : undefined, items);
        }

        return mergeById(prev, items);
      });
    };

    const cachedResult = requirementListCache.get(cacheKey);
    if (cachedResult) {
      // 命中缓存时直接回填，不再请求。
      setListLoading(false);
      applyResult(cachedResult);
      return;
    }

    setListLoading(true);

    const pendingRequest =
      requirementListRequestCache.get(cacheKey) ||
      requirementList({
        pagination: { offset: (page - 1) * REQUIREMENT_PAGE_SIZE, take: REQUIREMENT_PAGE_SIZE },
        body: {
          keyword: nextKeyword,
          states: REQUIREMENT_PASSED_STATES,
          status: RequirementStatusEnum.OPEN_PENDING,
        },
      })
        .then((res) => {
          const items = res.body ?? [];
          const nextResult = {
            items,
            total: res.pagination?.size ?? items.length,
          };
          // 只缓存接口原始结果，选中项合并留到 applyResult 里做。
          requirementListCache.set(cacheKey, nextResult);
          return nextResult;
        })
        .finally(() => {
          requirementListRequestCache.delete(cacheKey);
        });

    requirementListRequestCache.set(cacheKey, pendingRequest);

    pendingRequest
      .then((result) => {
        applyResult(result);
      })
      .catch(() => {
        if (fetchId !== requirementFetchIdRef.current || page !== 1) {
          return;
        }
        setRequirements(selectedRequirement ? [selectedRequirement] : []);
        setTotal(0);
      })
      .finally(() => {
        if (fetchId === requirementFetchIdRef.current) {
          setListLoading(false);
        }
      });
  };

  const loadRequirementItems = async (nextRequirementId: string) => {
    if (!nextRequirementId) {
      setRequirementItems([]);
      return;
    }

    if (requirementItemsCache.has(nextRequirementId)) {
      setRequirementItems(requirementItemsCache.get(nextRequirementId) || []);
      return;
    }

    const fetchId = ++itemFetchIdRef.current;
    setListLoading(true);

    const pendingRequest =
      requirementItemsRequestCache.get(nextRequirementId) ||
      requirementItemsFetch(nextRequirementId)
        .then((res) => {
          requirementItemsCache.set(nextRequirementId, res);
          return res;
        })
        .finally(() => {
          requirementItemsRequestCache.delete(nextRequirementId);
        });

    requirementItemsRequestCache.set(nextRequirementId, pendingRequest);

    try {
      const nextItems = await pendingRequest;
      if (
        fetchId === itemFetchIdRef.current &&
        latestRequirementIdRef.current === nextRequirementId
      ) {
        setRequirementItems(nextItems);
      }
    } catch {
      if (
        fetchId === itemFetchIdRef.current &&
        latestRequirementIdRef.current === nextRequirementId
      ) {
        setRequirementItems([]);
      }
    } finally {
      if (
        fetchId === itemFetchIdRef.current &&
        latestRequirementIdRef.current === nextRequirementId
      ) {
        setListLoading(false);
      }
    }
  };

  const requirementOptions = useMemo<RequirementSelectOption[]>(
    () =>
      requirements.map((item) => ({
        value: item.id,
        label: formatRequirementLabel(item, t),
        entity: item,
      })),
    [requirements, t],
  );

  const requirementItemOptions = useMemo<RequirementSelectOption[]>(() => {
    const excludedItemIds = new Set(excludeIds ?? []);
    if (value) {
      excludedItemIds.delete(value);
    }

    return requirementItems
      .filter((item) => {
        if (excludedItemIds.has(item.id)) {
          return false;
        }
        if (!keyword) {
          return true;
        }
        return matchRequirementItemKeyword(item, keyword);
      })
      .map((item) => ({
        value: item.id,
        label: formatRequirementItemLabel(item, t),
        entity: item,
      }));
  }, [excludeIds, keyword, requirementItems, t, value]);

  const options = isItemSelect ? requirementItemOptions : requirementOptions;

  const handleSearch = (nextKeyword: string) => {
    const trimmedKeyword = nextKeyword.trim();
    setKeyword(trimmedKeyword);
    onSearchProp?.(nextKeyword);

    if (isItemSelect) {
      return;
    }

    setCurrentPage(1);
    fetchRequirements(trimmedKeyword, 1);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    onPopupScrollProp?.(event);
    if (isItemSelect) {
      return;
    }

    const target = event.target as HTMLDivElement;
    if (
      listLoading ||
      currentPage * REQUIREMENT_PAGE_SIZE >= total ||
      target.scrollTop + target.offsetHeight < target.scrollHeight - 1
    ) {
      return;
    }

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchRequirements(keyword, nextPage);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    onDropdownVisibleChangeProp?.(open);
    if (!open) {
      return;
    }

    setKeyword("");
    if (isItemSelect) {
      if (requirementId) {
        loadRequirementItems(requirementId);
      }
      return;
    }

    setCurrentPage(1);
    fetchRequirements("", 1);
  };

  const handleChange = (nextValue: string | undefined, option?: RequirementSelectOption) => {
    if (!nextValue) {
      onChange?.(undefined);
      if (entityType === "requirement") {
        setSelectedRequirement(null);
        (onEntityChange as ((entity: Requirement | null) => void) | undefined)?.(null);
      } else {
        (onEntityChange as ((entity: RequirementItem | null) => void) | undefined)?.(null);
      }
      return;
    }

    const entity = option?.entity;
    onChange?.(nextValue);
    if (entityType === "requirement") {
      const requirementEntity = entity as Requirement | undefined;
      setSelectedRequirement(requirementEntity || null);
      (onEntityChange as ((entity: Requirement | null) => void) | undefined)?.(
        requirementEntity || null,
      );
      return;
    }

    (onEntityChange as ((entity: RequirementItem | null) => void) | undefined)?.(
      (entity as RequirementItem | undefined) || null,
    );
  };

  return (
    <Select
      {...selectProps}
      allowClear={selectProps.allowClear ?? true}
      disabled={disabled || (isItemSelect && !requirementId)}
      filterOption={false}
      loading={listLoading}
      notFoundContent={listLoading ? <Spin size="small" /> : null}
      onChange={(nextValue, option) =>
        handleChange(nextValue, Array.isArray(option) ? undefined : (option as RequirementSelectOption))
      }
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onPopupScroll={handleScroll}
      onSearch={handleSearch}
      // 需求项下拉按“名称 / 型号 / 编码”三段展示，右侧编码固定，便于在长型号场景下快速确认。
      optionRender={({ data }) =>
        entityType === "requirement" ? (
          <Flex align="center" justify="space-between" gap={8} style={{ width: "100%" }}>
            <span>{(data.entity as Requirement).saleOrder?.innerNumber || "-"}</span>
            <Tag color="blue">{(data.entity as Requirement).number || "-"}</Tag>
          </Flex>
        ) : (
          <Flex align="center" gap={8} style={{ width: "100%", minWidth: 0 }}>
            <div style={{ flex: "0 1 140px", minWidth: 0 }}>
              <SText
                text={(data.entity as RequirementItem).product?.name || "-"}
                style={{ display: "block" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <SText
                text={(data.entity as RequirementItem).product?.model || "-"}
                style={{ display: "block" }}
              />
            </div>
            <Tag color="blue" style={{ flexShrink: 0, marginInlineStart: "auto" }}>
              {(data.entity as RequirementItem).product?.code || "-"}
            </Tag>
          </Flex>
        )
      }
      options={options}
      // 需求项选择器保持与输入框同宽，避免三列内容被窄弹层压坏；需求单选择器仍允许自适应宽度。
      popupMatchSelectWidth={isItemSelect}
      showSearch
      style={{ width: "100%" }}
      dropdownRender={(menu) => (
        <>
          {dropdownRenderProp ? dropdownRenderProp(menu) : menu}
          {listLoading && options.length > 0 && (
            <Flex
              align="center"
              justify="center"
              style={{ width: "100%", padding: 10 }}
            >
              <Spin size="small" />
            </Flex>
          )}
        </>
      )}
    />
  );
};

export default RequirementSelect;
export type { RequirementSelectProps, RequirementSelectBaseProps };
